import type { ReactNode } from 'react'

export type MetricCardValueTone = 'default' | 'danger'

/** Semantic trend for the supporting line — not layout position. */
export type MetricCardTrend = 'positive' | 'negative' | 'neutral' | 'muted'

/**
 * Delta badge beside the value (Marketing Campaign Overview).
 * `increase` = green pill; `decrease` = red pill.
 */
export type MetricCardDeltaTone = 'increase' | 'decrease'

export type MetricCardSize = 'md' | 'lg'

export type MetricCardVariant = 'card' | 'plain'

/** Label color tone. `primary` = blue title (compact Dashboard header-badge card). */
export type MetricCardLabelTone = 'default' | 'primary'

/** Icon-only status pill in the header (e.g. green check). */
export type MetricCardStatusBadge = 'success'

/** Where the delta badge is shown: beside the value, or in the header right. */
export type MetricCardDeltaPlacement = 'value' | 'header'

export type MetricCardPreviewState =
  | 'default'
  | 'positive-trend'
  | 'negative-trend'
  | 'value-danger'
  | 'with-info'
  | 'with-unit'
  | 'with-warning'
  | 'with-delta'
  | 'header-badges'
  | 'size-lg'
  | 'plain'
  | 'no-footer'

export const METRIC_CARD_PREVIEW_STATES: MetricCardPreviewState[] = [
  'default',
  'positive-trend',
  'negative-trend',
  'value-danger',
  'with-info',
  'with-unit',
  'with-warning',
  'with-delta',
  'header-badges',
  'size-lg',
  'plain',
  'no-footer',
]

export const METRIC_CARD_PREVIEW_STATE_LABELS: Record<
  MetricCardPreviewState,
  string
> = {
  default: 'Default',
  'positive-trend': 'Positive trend',
  'negative-trend': 'Negative trend',
  'value-danger': 'Value danger',
  'with-info': 'With info',
  'with-unit': 'With unit',
  'with-warning': 'With warning',
  'with-delta': 'With delta badge',
  'header-badges': 'Header badges (Scheduled)',
  'size-lg': 'Large (Marketing)',
  plain: 'Plain (no border)',
  'no-footer': 'No footer',
}

export const METRIC_CARD_DEMO_LABEL = 'Production'
export const METRIC_CARD_DEMO_VALUE = '$42,850'
export const METRIC_CARD_DEMO_FOOTER = '12% vs last week'
export const METRIC_CARD_DEMO_UNIT = 'visits'
export const METRIC_CARD_DEMO_DELTA = 12
export const METRIC_CARD_DEMO_PREVIOUS_FOOTER = 'Previously 1,104'

export type MetricCardProps = {
  label?: string
  labelIcon?: ReactNode
  /** Label color tone. `primary` = blue title (compact header-badge card). */
  labelTone?: MetricCardLabelTone
  rightLabel?: string
  value?: ReactNode
  valueOver?: ReactNode
  /** Secondary unit shown beside the value (Reports KPI pattern). */
  unitInline?: string
  /**
   * Percent change badge beside the value (Marketing Overview).
   * Numbers are formatted as `+N%` / `-N%` from `deltaTone`.
   */
  delta?: number | string
  deltaTone?: MetricCardDeltaTone
  /** `value` (default) = badge beside value; `header` = badge in header right with an arrow. */
  deltaPlacement?: MetricCardDeltaPlacement
  /** Show a directional arrow inside the delta badge (used with `header` placement). */
  deltaShowArrow?: boolean
  /** Icon-only status pill in the header right (e.g. green check). */
  statusBadge?: MetricCardStatusBadge
  valueTone?: MetricCardValueTone
  footer?: ReactNode
  /** When `false`, hides the footer even if `footer` text or a preview state would show it. */
  showFooter?: boolean
  /**
   * Semantic trend for the footer line.
   * `positive` / `negative` show an up/down icon; `neutral` / `muted` are text-only.
   */
  trend?: MetricCardTrend
  infoTooltip?: string
  showInfoIcon?: boolean
  showWarningIcon?: boolean
  /**
   * `md` = Dashboard/Reports (24px value). `lg` = Marketing Overview (36px value).
   */
  size?: MetricCardSize
  /**
   * `card` = bordered tile (default). `plain` = content only for embedding in SectionCard.
   */
  variant?: MetricCardVariant
  className?: string
  darkMode?: boolean
  /** Static visual state for the design-system showcase only. */
  previewState?: MetricCardPreviewState
}

export type MetricCardGridColumns = 2 | 3 | 4 | 5 | 6 | 7

export type MetricCardGridProps = {
  children: ReactNode
  columns?: MetricCardGridColumns
  className?: string
}

/** Dashboard metric data shape accepted by `metricCardFromDashboard`. */
export type DashboardMetricLike = {
  id?: string
  leftLabel: string
  rightLabel?: string
  value: string | number
  valueOver?: string | number
  infoTooltip?: string
  footer?: string
  footerTrend?: 'up' | 'down' | 'neutral' | null
  showInfoIcon?: boolean
  showWarningIcon?: boolean
  valueTone?: 'danger'
  className?: string
}

/** Reports KPI data shape accepted by `metricCardFromReportsKpi`. */
export type ReportsKpiLike = {
  id: string
  title: string
  value: string
  unitInline?: string
  footer: string
  tone?: 'neutral' | 'positive'
  valueTone?: 'default' | 'danger'
}

/** Marketing Campaign Overview metric shape. */
export type MarketingMetricLike = {
  label: string
  value: string | number
  previous?: string | number
  percentage?: number
  status?: 'increase' | 'decrease' | 'neutral'
  infoTooltip?: string
  /** Append `%` to the primary value (e.g. delivery rate). */
  valueAsPercent?: boolean
}
